import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useEditAddressBook } from '@/hooks/organization/use-edit-address-book';
import { getRequestErrorMessage } from '@/lib/api/get-request-error-message';
import type { AddressBookListEntry } from '@/types/address-book';

interface EditAddressBookEntryNameDialogProps {
  open: boolean;
  entry: AddressBookListEntry | null;
  onOpenChange: (open: boolean) => void;
  onUpdated: () => void;
}

export function EditAddressBookEntryNameDialog({
  open,
  entry,
  onOpenChange,
  onUpdated,
}: EditAddressBookEntryNameDialogProps) {
  const [name, setName] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { editAddressBook, isLoading } = useEditAddressBook();

  useEffect(() => {
    if (!open || !entry) {
      setErrorMessage(null);
      return;
    }

    setName(entry.name);
    setErrorMessage(null);
  }, [open, entry]);

  const trimmedName = name.trim();
  const currentName = entry?.name.trim() ?? '';
  const isSubmitDisabled =
    !entry ||
    entry.type === 'MLSACCOUNT' ||
    trimmedName.length === 0 ||
    trimmedName === currentName ||
    isLoading;

  const handleSubmit = async () => {
    if (!entry || isSubmitDisabled) {
      return;
    }

    setErrorMessage(null);

    try {
      await editAddressBook({
        instructions: [
          {
            action: 'upsert',
            address: entry.address,
            name: trimmedName,
          },
        ],
      });
      onUpdated();
      onOpenChange(false);
    } catch (error) {
      setErrorMessage(
        getRequestErrorMessage(error, 'Failed to update address book entry.')
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit label</DialogTitle>
          <DialogDescription>
            Update the saved name for this address book entry.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="address-book-entry-edit-name">Name</Label>
          <Input
            id="address-book-entry-edit-name"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              setErrorMessage(null);
            }}
            placeholder="Treasury Safe"
          />
        </div>

        {errorMessage ? (
          <div className="text-sm text-rose-600">{errorMessage}</div>
        ) : null}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            className="relative"
          >
            <span className={isLoading ? 'invisible' : ''}>Save</span>
            {isLoading ? (
              <span className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="h-4 w-4 animate-spin" />
              </span>
            ) : null}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
